// ─────────────────────────────────────────────
//  nextRoom — team routing between rooms ("Next Room").
//  A team's destination is stored ON its day's assignment row
//  (nextRoomId / nextRoomName), exactly like relief. The incoming side is
//  always DERIVED from the live assignment rows — never double-written —
//  so the two cards can't drift apart.
// ─────────────────────────────────────────────
import { normalizeStatus } from "./status";
import { assignmentForRoom } from "./gridTemplate";

const norm = (s) => (s || "").toString().trim().toLowerCase();

// The outgoing link staged on an assignment row, or null when none is set.
export function nextRoomLink(assignment) {
  if (!assignment || !assignment.nextRoomId) return null;
  return {
    roomId: assignment.nextRoomId,
    roomName: assignment.nextRoomName || "",
  };
}

// Compact "who is coming" label for a routed team — CRNA first (the room's
// person), the attending after. Falls back to whichever exists.
export function teamLabel(assignment) {
  if (!assignment) return "";
  const parts = [assignment.ahpName, assignment.attendingName].filter(Boolean);
  return parts.join(" · ");
}

// Every team routed TO this room, derived from today's assignment rows.
// A row never reports itself as incoming to its own room (self-link guard).
export function incomingTeamsForRoom(room, assignments) {
  if (!room?.id) return [];
  const list = Array.isArray(assignments) ? assignments : [];
  return list
    .filter(
      (a) =>
        a &&
        a.nextRoomId === room.id &&
        norm(a.orRoom) !== norm(room.name)
    )
    .map((a) => ({
      assignmentId: a.id || null,
      fromRoomName: a.orRoom || "",
      ahpId: a.ahpId || null,
      attendingId: a.attendingId || null,
      label: teamLabel(a),
    }));
}

// Dropdown choices for the Next Room picker: every OTHER room on the board,
// numerically sorted (OR 2 before OR 10), flagged when a team already runs it.
// Occupancy comes from the day's ASSIGNMENT ROWS first (the source of truth —
// room docs often stay "empty" while the sheet import staffs the whole board),
// with the live room-doc fields as fallback.
export function nextRoomChoices(rooms, currentRoomId, assignments = []) {
  const list = Array.isArray(rooms) ? rooms : [];
  return list
    .filter((r) => r && r.id && r.id !== currentRoomId)
    .map((r) => {
      const row = assignmentForRoom(r.name, assignments);
      return {
        id: r.id,
        name: r.name || "",
        team: teamLabel(row),
        inUse:
          !!(row && (row.ahpName || row.attendingName)) ||
          !!(r.providerName || r.supervisorName) ||
          ["active", "closing"].includes(normalizeStatus(r.status)),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

// True when this room's own assignment already carries the incoming team —
// i.e. the row was pre-staged by routing to an empty room ("brand new card").
// The destination then announces "Team from OR X" instead of "Incoming from".
export function isStagedArrival(assignment, incoming) {
  if (!assignment || !incoming) return false;
  if (incoming.ahpId) return assignment.ahpId === incoming.ahpId;
  if (incoming.attendingId) return assignment.attendingId === incoming.attendingId;
  return false;
}

// The picker's two sections: empty rooms the team can simply move into, and
// scheduled (occupied) rooms the team may take over. Every room appears in
// exactly one group.
export function nextRoomChoiceGroups(rooms, currentRoomId, assignments = []) {
  const choices = nextRoomChoices(rooms, currentRoomId, assignments);
  return {
    available: choices.filter((c) => !c.inUse),
    scheduled: choices.filter((c) => c.inUse),
  };
}
